import React, { useEffect, useState } from 'react';
import {
  superAdminGetAdmins,
  superAdminCreateAdmin,
  superAdminRemoveAdmin,
  superAdminGetPermissions,
  superAdminGetRolePermissions,
  superAdminUpdateAdminPermissions,
} from '../../api/api';

export default function SuperAdminAdmins() {
  const [admins, setAdmins] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [enabled, setEnabled] = useState([]);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '' });

  const load = () => {
    superAdminGetAdmins().then((r) => setAdmins(r.data.data || [])).catch(() => {});
    superAdminGetPermissions().then((r) => setPermissions(r.data.data || [])).catch(() => {});
    superAdminGetRolePermissions('admin').then((r) => {
      const on = (r.data.data || []).filter((x) => Number(x.granted) === 1).map((x) => x.code);
      setEnabled(on);
    }).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const createAdmin = async (e) => {
    e.preventDefault();
    await superAdminCreateAdmin(form);
    setForm({ first_name: '', last_name: '', email: '', phone: '', password: '' });
    load();
  };

  const removeAdmin = async (id) => {
    if (!window.confirm('Remove admin rights from this account?')) return;
    await superAdminRemoveAdmin(id);
    load();
  };

  const savePermissions = async () => {
    const firstAdmin = admins.find((a) => a.role === 'admin');
    if (!firstAdmin) return;
    await superAdminUpdateAdminPermissions(firstAdmin.id, { permissions: enabled });
    alert('Admin permission policy updated');
  };

  const togglePermission = (code) => {
    setEnabled((prev) => (prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]));
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="superadmin-title text-3xl font-bold">Admin Governance</h1>
        <p className="text-slate-500">Create or remove admins, and control enterprise permissions</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <form className="superadmin-glass rounded-2xl p-4 space-y-3" onSubmit={createAdmin}>
          <p className="font-semibold">Create New Admin</p>
          <input required className="ems-input" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          <input required className="ems-input" placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          <input required type="email" className="ems-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input className="ems-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input required type="password" className="ems-input" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <button className="w-full ems-btn-primary">Create Admin</button>
        </form>

        <div className="superadmin-glass rounded-2xl p-4 xl:col-span-2">
          <p className="font-semibold mb-3">Current Admin Accounts</p>
          <div className="overflow-auto">
            <table className="ems-table min-w-[640px]">
              <thead>
                <tr className="text-slate-500 text-left border-b border-slate-100">
                  <th className="py-2">Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr key={a.id} className="border-b border-slate-50">
                    <td className="py-2.5">{a.name}</td>
                    <td>{a.email}</td>
                    <td>{a.role}</td>
                    <td>{a.last_login_at ? new Date(a.last_login_at).toLocaleString() : 'Never'}</td>
                    <td>
                      {a.role === 'admin' && (
                        <button className="px-3 py-1 rounded-lg text-xs bg-rose-500/20 hover:bg-rose-500/30" onClick={() => removeAdmin(a.id)}>
                          Remove Admin
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="superadmin-glass rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="font-semibold">Admin Permission Matrix</p>
          <button className="ems-btn-primary text-sm" onClick={savePermissions}>Save Permissions</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {permissions.map((p) => (
            <label key={p.code} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 flex gap-2 items-start">
              <input type="checkbox" checked={enabled.includes(p.code)} onChange={() => togglePermission(p.code)} className="mt-1" />
              <span>
                <span className="text-sm font-medium">{p.name}</span>
                <span className="block text-xs text-slate-500">{p.code} · {p.module || 'general'}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
